import apiClient from '../utils/axiosPetition';
import { flattenGroupedProfessions, FALLBACK_PROFESSIONS_GROUPED } from '../utils/professionService';
import { toSentenceCase } from '../utils/formatServiceName';

/** Especialidades del directorio (orden de visualización) */
export const ESPECIALIDADES_DIRECTORIO = [
    { value: 'medicina general', label: 'Médico general' },
    { value: 'medicina familiar', label: 'Médico familiar' },
    { value: 'fonoaudiología', label: 'Fonoaudiología' },
    { value: 'terapia física', label: 'Terapia física' },
    { value: 'terapeuta ocupacional', label: 'Terapeuta ocupacional' },
    { value: 'terapeuta respiratorio', label: 'Terapeuta respiratoria' },
    { value: 'enfermería', label: 'Enfermería' },
    { value: 'nutrición', label: 'Nutrición' },
    { value: 'psicología', label: 'Psicología' },
    { value: 'trabajo social', label: 'Trabajo social' },
];

const SERVICE_MAPPING = {
    'medicina general': ['medicina general', 'medicina_general', 'general', 'médico general'],
    'medicina familiar': ['medicina familiar', 'medicina_familiar', 'familiar', 'médico familiar'],
    'terapia física': ['terapia física', 'terapia_fisica', 'terapia fisica', 'fisioterapia', 'fisioterapeuta', 'fisio'],
    'terapeuta ocupacional': ['terapeuta ocupacional', 'terapia ocupacional', 'terapia_ocupacional'],
    'terapeuta respiratorio': ['terapeuta respiratorio', 'terapia respiratoria', 'terapia respiratorio'],
    'enfermería': ['enfermería', 'enfermeria', 'enfermero', 'enfermera'],
    'nutrición': ['nutrición', 'nutricion', 'nutricionista'],
    'psicología': ['psicología', 'psicologia', 'psicologo', 'psicólogo'],
    'fonoaudiología': ['fonoaudiología', 'fonoaudiologia', 'fono', 'terapia de lenguaje', 'terapia lenguaje'],
    'trabajo social': ['trabajo social', 'trabajo_social', 'trabajador social', 'social'],
    'medicina_general': ['medicina general', 'medicina_general', 'general'],
    'medicina_familiar': ['medicina familiar', 'medicina_familiar', 'familiar'],
    'terapia_fisica': ['terapia física', 'terapia_fisica', 'terapia fisica', 'fisioterapia', 'fisio'],
    'psicologia': ['psicología', 'psicologia', 'psicologo', 'psicólogo'],
    'nutricion': ['nutrición', 'nutricion', 'nutricionista'],
    'fonoaudiologia': ['fonoaudiología', 'fonoaudiologia', 'fono'],
    'trabajo_social': ['trabajo social', 'trabajo_social', 'social'],
    'enfermeria': ['enfermería', 'enfermeria', 'enfermero', 'enfermera'],
    'auxiliar_enfermeria': ['auxiliar de enfermería', 'auxiliar enfermeria', 'auxiliar enfermería']
};

class DoctorService {
    /**
     * Obtiene todos los doctores y filtra solo los PREMIUM
     * @param {boolean} isAuth - Si el usuario está autenticado
     * @param {string} [clinicIdFromContext] - clinicId del JWT/contexto (por si no está en localStorage, p. ej. paciente recién logueado)
     * @returns {Promise<Object>} - Objeto con doctores filtrados y estadísticas
     */
    async getPremiumDoctors(isAuth = false, clinicIdFromContext = null) {
        try {
            let response;
            
            if (!isAuth) {
                // Para usuarios no autenticados, usar el endpoint específico
                try {
                    response = await apiClient.get('/api/home/getPremiumDoctors');
                    console.log('✅ Usando endpoint específico para usuarios no autenticados');
                } catch (endpointError) {
                    console.log('⚠️ Endpoint específico no disponible (405/404), usando fallback');
                    // Fallback: usar endpoint general y filtrar en frontend
                    response = await apiClient.get('/api/home/getDoctors');
                    if (response.data) {
                        const raw = Array.isArray(response.data) ? response.data : [];
                        const allDoctors = raw.map(doc => ({ ...doc, membershipPlan: doc.membershipPlan || 'free' }));
                        const premiumCount = allDoctors.filter(d => d.membershipPlan === 'premium').length;
                        return {
                            doctors: allDoctors,
                            total: allDoctors.length,
                            premium: premiumCount,
                            success: true
                        };
                    }
                }
            } else {
                // clinicId: localStorage primero; si no hay (p. ej. paciente sin completar perfil / OAuth), usar contexto
                const stored = localStorage.getItem('clinicId');
                const fromContext = clinicIdFromContext != null && String(clinicIdFromContext).trim() !== '' ? String(clinicIdFromContext).trim() : null;
                const clinicId = (stored && stored !== 'null' && stored !== 'undefined') ? stored : fromContext;
                const hasValidClinic = clinicId && clinicId !== 'null' && clinicId !== 'undefined';
                if (!hasValidClinic) {
                    // Paciente sin clínica (p. ej. login con Google): usar endpoints públicos
                    try {
                        response = await apiClient.get('/api/home/getPremiumDoctors');
                    } catch (e) {
                        response = await apiClient.get('/api/home/getDoctors');
                        if (response && response.data) {
                            const raw = Array.isArray(response.data) ? response.data : [];
                            const allDoctors = raw.map(doc => ({ ...doc, membershipPlan: doc.membershipPlan || 'free' }));
                            const premiumCount = allDoctors.filter(d => d.membershipPlan === 'premium').length;
                            return {
                                doctors: allDoctors,
                                total: allDoctors.length,
                                premium: premiumCount,
                                success: true
                            };
                        }
                    }
                } else {
                try {
                    response = await apiClient.get(`/api/patient/getPremiumDoctors/${clinicId}`);
                    console.log('✅ Usando endpoint específico para usuarios autenticados');
                } catch (endpointError) {
                    try {
                        response = await apiClient.get(`/api/patient/getDoctors/${clinicId}`);
                        if (response?.data) {
                            // getDoctors devuelve array sin membershipPlan; normalizar para que el UI los muestre (p. ej. en "Otros profesionales")
                            const raw = Array.isArray(response.data) ? response.data : [];
                            const allDoctors = raw.map(doc => ({ ...doc, membershipPlan: doc.membershipPlan || 'free' }));
                            const premiumCount = allDoctors.filter(d => d.membershipPlan === 'premium').length;
                            return {
                                doctors: allDoctors,
                                total: allDoctors.length,
                                premium: premiumCount,
                                success: true
                            };
                        }
                    } catch (_) {}
                    // Si falla (clinicId inválido, clínica sin doctores, etc.): usar endpoint público para que el paciente vea doctores
                    try {
                        response = await apiClient.get('/api/home/getPremiumDoctors');
                        console.log('🔄 Fallback a endpoint público tras error en endpoint por clínica');
                    } catch (e) {
                        response = await apiClient.get('/api/home/getDoctors');
                        if (response?.data) {
                            const raw = Array.isArray(response.data) ? response.data : [];
                            const allDoctors = raw.map(doc => ({ ...doc, membershipPlan: doc.membershipPlan || 'free' }));
                            const premiumCount = allDoctors.filter(d => d.membershipPlan === 'premium').length;
                            return {
                                doctors: allDoctors,
                                total: allDoctors.length,
                                premium: premiumCount,
                                success: true
                            };
                        }
                    }
                }
                }
            }

            if (response.data) {
                // El backend ya devuelve solo doctores PREMIUM con estadísticas
                return {
                    doctors: response.data.doctors || response.data,
                    total: response.data.total || 0,
                    premium: response.data.premium || response.data.length || 0,
                    success: true
                };
            }
            
            return {
                doctors: [],
                total: 0,
                premium: 0,
                success: false
            };
        } catch (error) {
            console.error('❌ Error obteniendo doctores PREMIUM:', error);
            throw error;
        }
    }

    /**
     * Obtiene todos los doctores sin filtrar
     * @param {boolean} isAuth - Si el usuario está autenticado
     * @returns {Promise<Array>} - Array de todos los doctores
     */
    async getAllDoctors(isAuth = false) {
        try {
            let response;
            const clinicId = localStorage.getItem('clinicId');
            const hasValidClinic = clinicId && clinicId !== 'null' && clinicId !== 'undefined';

            if (!isAuth || !hasValidClinic) {
                response = await apiClient.get('/api/home/getDoctors');
            } else {
                response = await apiClient.get(`/api/patient/getDoctors/${clinicId}`);
            }

            return response.data || [];
        } catch (error) {
            console.error('Error obteniendo todos los doctores:', error);
            throw error;
        }
    }

    normalizeServiceValue(service) {
        if (!service) return '';
        return service.toLowerCase().replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
    }

    doctorMatchesService(doctorServiceValue, searchService) {
        if (!doctorServiceValue || !searchService) return false;

        const doctorService = this.normalizeServiceValue(doctorServiceValue);
        const search = searchService.toLowerCase();

        if (doctorService === search) return true;
        if (doctorService.includes(search) || search.includes(doctorService)) return true;

        const mappedServices = SERVICE_MAPPING[search] || [];
        return mappedServices.some(mappedService => doctorService.includes(mappedService));
    }

    resolveDoctorSpecialtyKey(service) {
        if (!service) return 'otras';

        for (const esp of ESPECIALIDADES_DIRECTORIO) {
            if (this.doctorMatchesService(service, esp.value)) {
                return esp.value;
            }
        }

        return 'otras';
    }

    getSpecialtyLabel(specialtyKey) {
        const known = ESPECIALIDADES_DIRECTORIO.find(esp => esp.value === specialtyKey);
        if (known) return known.label;
        if (specialtyKey === 'otras') return 'Otras especialidades';
        return toSentenceCase(specialtyKey.replace(/_/g, ' '));
    }

    sortDoctorsForDisplay(doctors) {
        return [...doctors].sort((a, b) => {
            const aPremium = a.membershipPlan === 'premium' ? 0 : 1;
            const bPremium = b.membershipPlan === 'premium' ? 0 : 1;
            if (aPremium !== bPremium) return aPremium - bPremium;
            return (a.fullName || '').localeCompare(b.fullName || '', 'es', { sensitivity: 'base' });
        });
    }

    groupDoctorsBySpecialty(doctors) {
        const groups = new Map();

        doctors.forEach(doc => {
            const key = this.resolveDoctorSpecialtyKey(doc.service);
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push(doc);
        });

        const result = [];

        ESPECIALIDADES_DIRECTORIO.forEach(esp => {
            const docs = groups.get(esp.value);
            if (docs?.length) {
                result.push({
                    key: esp.value,
                    label: esp.label,
                    doctors: this.sortDoctorsForDisplay(docs),
                });
                groups.delete(esp.value);
            }
        });

        const otras = groups.get('otras');
        if (otras?.length) {
            result.push({
                key: 'otras',
                label: 'Otras especialidades',
                doctors: this.sortDoctorsForDisplay(otras),
            });
            groups.delete('otras');
        }

        groups.forEach((docs, key) => {
            if (docs.length) {
                result.push({
                    key,
                    label: this.getSpecialtyLabel(key),
                    doctors: this.sortDoctorsForDisplay(docs),
                });
            }
        });

        return result;
    }

    /**
     * Filtra doctores por servicio
     * @param {Array} doctors - Array de doctores
     * @param {string} service - Servicio a filtrar
     * @returns {Array} - Doctores filtrados por servicio
     */
    filterDoctorsByService(doctors, service) {
        if (!service) return doctors;

        return doctors.filter(doc => this.doctorMatchesService(doc.service, service));
    }

    /**
     * Obtiene doctores por especialidad/servicio
     * @param {string} especialidad - Especialidad médica
     * @param {boolean} isAuth - Si el usuario está autenticado
     * @returns {Promise<Object>} - Objeto con doctores de la especialidad
     */
    async getDoctorsBySpecialty(especialidad, isAuth = false) {
        console.log('🏥 DoctorService - Obteniendo doctores por especialidad:', especialidad);
        console.log('🏥 DoctorService - Usuario autenticado:', isAuth);
        
        try {
            if (!especialidad) {
                console.log('❌ DoctorService - Especialidad requerida');
                return {
                    doctors: [],
                    success: false,
                    message: 'Especialidad requerida'
                };
            }

            let response;
            
            if (!isAuth) {
                console.log('🌐 DoctorService - Usando endpoint público');
                // Para usuarios no autenticados, usar endpoint público
                try {
                    console.log('🔗 DoctorService - Intentando endpoint específico público');
                    response = await apiClient.get(`/api/home/getDoctorsBySpecialty?specialty=${especialidad}`);
                    console.log('✅ DoctorService - Respuesta endpoint específico:', response.data);
                } catch (endpointError) {
                    console.log('⚠️ DoctorService - Endpoint específico no disponible, usando fallback');
                    console.log('🔗 DoctorService - Obteniendo todos los doctores públicos');
                    // Fallback: obtener todos los doctores y filtrar
                    const allDoctorsResponse = await apiClient.get('/api/home/getDoctors');
                    console.log('📊 DoctorService - Todos los doctores públicos:', allDoctorsResponse.data);
                    const allDoctors = allDoctorsResponse.data || [];
                    const filteredDoctors = this.filterDoctorsByService(allDoctors, especialidad);
                    console.log('🔍 DoctorService - Doctores filtrados:', filteredDoctors);
                    return {
                        doctors: filteredDoctors,
                        success: true,
                        total: filteredDoctors.length
                    };
                }
            } else {
                console.log('🔐 DoctorService - Usando endpoint autenticado');
                const clinicId = localStorage.getItem('clinicId');
                const hasValidClinic = clinicId && clinicId !== 'null' && clinicId !== 'undefined';
                console.log('🏥 DoctorService - Clinic ID:', clinicId);
                if (!hasValidClinic) {
                    // Paciente sin clínica (p. ej. Google): usar endpoint público
                    try {
                        response = await apiClient.get(`/api/home/getDoctorsBySpecialty?specialty=${especialidad}`);
                    } catch (e) {
                        const allDoctorsResponse = await apiClient.get('/api/home/getDoctors');
                        const allDoctors = allDoctorsResponse.data || [];
                        const filteredDoctors = this.filterDoctorsByService(allDoctors, especialidad);
                        return {
                            doctors: filteredDoctors,
                            success: true,
                            total: filteredDoctors.length
                        };
                    }
                } else {
                    try {
                        response = await apiClient.get(`/api/patient/getDoctorsBySpecialty/${clinicId}?specialty=${especialidad}`);
                    } catch (endpointError) {
                        const allDoctorsResponse = await apiClient.get(`/api/patient/getDoctors/${clinicId}`);
                        const allDoctors = allDoctorsResponse.data || [];
                        const filteredDoctors = this.filterDoctorsByService(allDoctors, especialidad);
                        return {
                            doctors: filteredDoctors,
                            success: true,
                            total: filteredDoctors.length
                        };
                    }
                }
            }

            const result = {
                doctors: response.data.doctors || response.data || [],
                success: true,
                total: response.data.total || (response.data.doctors ? response.data.doctors.length : response.data.length) || 0
            };
            
            console.log('✅ DoctorService - Resultado final:', result);
            return result;

        } catch (error) {
            console.error('❌ DoctorService - Error obteniendo doctores por especialidad:', error);
            return {
                doctors: [],
                success: false,
                message: error.response?.data?.message || 'Error al obtener doctores por especialidad',
                error: error
            };
        }
    }

    /**
     * Obtiene todas las especialidades disponibles
     * @returns {Promise<Array>} - Array de especialidades
     */
    async getAvailableSpecialties() {
        try {
            const response = await apiClient.get('/api/professions');
            if (Array.isArray(response.data?.flat) && response.data.flat.length > 0) {
                return response.data.flat;
            }
            const legacy = await apiClient.get('/api/home/getSpecialties');
            return legacy.data || [];
        } catch (error) {
            console.error('❌ Error obteniendo especialidades:', error);
            return flattenGroupedProfessions(FALLBACK_PROFESSIONS_GROUPED);
        }
    }

    /**
     * Obtiene información detallada de un doctor
     * @param {string} doctorId - ID del doctor
     * @param {boolean} isAuth - Si el usuario está autenticado
     * @returns {Promise<Object>} - Información detallada del doctor
     */
    async getDoctorDetails(doctorId, isAuth = false) {
        try {
            if (!doctorId) {
                return {
                    success: false,
                    message: 'ID del doctor requerido'
                };
            }

            let response;
            
            const clinicId = localStorage.getItem('clinicId');
            const hasValidClinic = clinicId && clinicId !== 'null' && clinicId !== 'undefined';
            if (!isAuth || !hasValidClinic) {
                response = await apiClient.get(`/api/home/getDoctorDetails/${doctorId}`);
            } else {
                response = await apiClient.get(`/api/patient/getDoctorDetails/${clinicId}/${doctorId}`);
            }

            return {
                doctor: response.data,
                success: true
            };

        } catch (error) {
            console.error('❌ Error obteniendo detalles del doctor:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Error al obtener detalles del doctor',
                error: error
            };
        }
    }

    /**
     * Busca doctores por nombre o especialidad
     * @param {string} searchTerm - Término de búsqueda
     * @param {boolean} isAuth - Si el usuario está autenticado
     * @returns {Promise<Object>} - Doctores encontrados
     */
    async searchDoctors(searchTerm, isAuth = false) {
        try {
            if (!searchTerm || searchTerm.trim().length < 2) {
                return {
                    doctors: [],
                    success: false,
                    message: 'Término de búsqueda debe tener al menos 2 caracteres'
                };
            }

            let response;
            
            const clinicId = localStorage.getItem('clinicId');
            const hasValidClinic = clinicId && clinicId !== 'null' && clinicId !== 'undefined';
            if (!isAuth || !hasValidClinic) {
                response = await apiClient.get(`/api/home/searchDoctors?q=${encodeURIComponent(searchTerm)}`);
            } else {
                response = await apiClient.get(`/api/patient/searchDoctors/${clinicId}?q=${encodeURIComponent(searchTerm)}`);
            }

            return {
                doctors: response.data.doctors || response.data || [],
                success: true,
                total: response.data.total || (response.data.doctors ? response.data.doctors.length : response.data.length) || 0
            };

        } catch (error) {
            console.error('❌ Error buscando doctores:', error);
            return {
                doctors: [],
                success: false,
                message: error.response?.data?.message || 'Error al buscar doctores',
                error: error
            };
        }
    }
}

export default new DoctorService(); 